import { Box } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useLayoutEffect, useRef } from "react";
import type { UIMatch } from "react-router";
import { useLocation, useMatches } from "react-router";

import useDesktopLayout from "../../hooks/useDesktopLayout";
import type { GaiaRouteHandle } from "../../types/routeHandle";
import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from "../AppDrawer/DesktopDrawer";
import { GaiaBreadcrumbs } from "../GaiaBreadcrumbs/GaiaBreadcrumbs";
import { TOPBAR_HEIGHT } from "../GaiaTopMenu";
import { ContentFrame } from "./ContentFrame";

/** Normal layout for all routes: ContentFrame + sticky breadcrumbs bar + scroll container. */
export function ContentLayout({ children }: PropsWithChildren) {
    const { isSidebarCollapsed } = useDesktopLayout();
    const matches = useMatches() as Array<UIMatch<unknown, GaiaRouteHandle>>;
    const hasBreadcrumbs = matches.some(match => match.handle?.breadcrumb !== undefined);
    const scrollRef = useRef<HTMLDivElement>(null);
    const dividerRef = useRef<HTMLDivElement>(null);
    const { pathname } = useLocation();

    // biome-ignore lint/correctness/useExhaustiveDependencies: pathname triggers the scroll reset on navigation.
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = 0;
    }, [pathname]);

    // Fade the divider in over the first 50px of scroll.
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (dividerRef.current) {
            dividerRef.current.style.opacity = String(Math.min(1, event.currentTarget.scrollTop / 50));
        }
    };

    return (
        <ContentFrame topbarHeight={TOPBAR_HEIGHT} sidebarWidth={isSidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}>
            <Box ref={scrollRef} onScroll={handleScroll} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {hasBreadcrumbs && (
                    <Box
                        sx={theme => ({
                            position: "sticky",
                            top: 0,
                            zIndex: theme.zIndex.appBar,
                            px: { xs: 1, sm: 2 },
                            py: 1,
                            backgroundColor: theme.palette.background.paper,
                        })}
                    >
                        <GaiaBreadcrumbs key={pathname} />
                        <Box
                            ref={dividerRef}
                            sx={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: "100%",
                                zIndex: 1,
                                borderTop: "1px solid",
                                borderColor: "divider",
                                opacity: 0,
                            }}
                        />
                    </Box>
                )}
                {children}
            </Box>
        </ContentFrame>
    );
}
